// Pure helpers for the composer and list sends: schedule presets, addresses, drafts, attachment sizes and wording.
// Nothing here touches the DOM, so the tests in logic.test.ts run under node.

import { daysFromToday, formatBytes, formatDay, formatDayTime, formatNumber, formatTime, plural } from '@/lib/format';
import { fillMergeFields, intervalLabel, MAX_MESSAGE_BYTES, type MergeValues } from '@shared/mail';

/* ---------- recipients ---------- */

export interface Rcpt {
  email: string;
  name: string | null;
  personId?: string | null;
}

const EMAIL_RE = /^[^\s@<>(),;:"[\]]+@[^\s@<>(),;:"[\]]+\.[^\s@<>(),;:"[\]]{2,}$/;

export function isEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

/**
 * Reads what someone typed or pasted into an address box: "a@x.com, b@y.com", "Hannah <h@x.com>" or
 * "\"Whitcombe, Hannah\" <h@x.com>". Returns the addresses it understood and the pieces it did not.
 */
export function parseAddresses(input: string): { valid: Rcpt[]; invalid: string[] } {
  const valid: Rcpt[] = [];
  const invalid: string[] = [];
  const tokens = input.match(/\s*"[^"]*"\s*<[^>]*>|[^,;\n]+/g) ?? [];
  for (const raw of tokens) {
    const token = raw.trim();
    if (!token) continue;
    const angled = /^"?([^"<]*?)"?\s*<([^>]+)>$/.exec(token);
    const email = (angled ? angled[2]! : token).trim().replace(/^mailto:/i, '');
    const name = angled ? angled[1]!.trim() || null : null;
    if (isEmail(email)) valid.push({ email: email.toLowerCase(), name });
    else invalid.push(token);
  }
  return { valid: dedupeRecipients(valid), invalid };
}

/** Keeps the first appearance of each address. */
export function dedupeRecipients<T extends { email: string }>(list: T[], taken: Iterable<string> = []): T[] {
  const seen = new Set([...taken].map((e) => e.toLowerCase()));
  return list.filter((r) => {
    const key = r.email.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** "Hannah Whitcombe", "Hannah Whitcombe and Jon Reyes", "Hannah Whitcombe and 2 others". */
export function whoLabel(list: Rcpt[]): string {
  const names = list.map((r) => r.name?.trim() || r.email);
  if (names.length === 0) return 'nobody yet';
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]} and ${plural(names.length - 1, 'other')}`;
}

function shortName(name: string): string {
  return name.replace(/^\s*(dr|prof)\.?\s+/i, '').trim();
}

export type ComposeMode = 'new' | 'reply' | 'replyAll' | 'forward';

export function composerTitle(mode: ComposeMode, opts: { name?: string | null; editing?: boolean } = {}): string {
  if (opts.editing) return 'Edit scheduled email';
  const name = opts.name ? shortName(opts.name) : '';
  if (mode === 'reply') return name ? `Reply to ${name}` : 'Reply';
  if (mode === 'replyAll') return 'Reply to all';
  if (mode === 'forward') return 'Forward conversation';
  return name ? `Email ${name}` : 'New email';
}

/* ---------- message bodies ---------- */

export function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Lines of text as the editor writes them: one div per line, an empty line as <div><br></div>. */
export function textToEditorHtml(text: string): string {
  return text
    .split('\n')
    .map((line) => (line ? `<div>${escapeHtml(line)}</div>` : '<div><br></div>'))
    .join('');
}

/** True when the HTML holds no words: only tags, line breaks and spaces. */
export function isBlankHtml(html: string): boolean {
  return (
    html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;|&#160;|\u00a0/g, ' ')
      .trim() === ''
  );
}

/** A link typed into the link box as a full address: a web address gets https, an email address mailto. */
export function normaliseUrl(input: string): string | null {
  const v = input.trim();
  if (!v || /\s/.test(v)) return null;
  if (/^(https?:\/\/|mailto:|tel:)/i.test(v)) return v;
  if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return null;
  if (isEmail(v)) return `mailto:${v}`;
  if (/^[^./@]+(\.[^./@]+)+(\/.*)?$/.test(v)) return `https://${v}`;
  return null;
}

/** The opening of a fresh email: a greeting with their first name, room to write and your sign-off. */
export function starterBody(first: string | null, myFirst: string | null): string {
  const lines = [first ? `Hi ${first},` : 'Hi,', '', '', 'Best,'];
  if (myFirst) lines.push(myFirst);
  return textToEditorHtml(lines.join('\n'));
}

const MERGE_ANY = /\{(first|first_name|company|my_first_name)\}/g;

/** The merge fields an email uses, in the order they first appear: "{first}", "{company}". */
export function mergeFieldsIn(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(MERGE_ANY)) {
    const key = m[1] === 'first_name' ? '{first}' : `{${m[1]}}`;
    if (!out.includes(key)) out.push(key);
  }
  return out;
}

/** Fills merge fields for a preview, wrapping each filled value so it stands out. Takes and returns HTML. */
export function highlightMergeFields(html: string, values: MergeValues): string {
  return html.replace(MERGE_ANY, (m) => `<span class="mfield">${fillMergeFields(m, values, 'html')}</span>`);
}

/* ---------- scheduling ---------- */

export interface SchedulePreset {
  id: string;
  label: string;
  sub: string;
  at: Date;
  icon: 'clock' | 'calendar';
}

function atLocal(now: Date, days: number, hours: number, minutes = 0): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + days, hours, minutes, 0, 0);
}

/** Quick choices in the schedule menu, in your own time: this afternoon, tomorrow morning and Monday morning. */
export function schedulePresets(now = new Date()): SchedulePreset[] {
  const out: SchedulePreset[] = [];
  if (now.getHours() < 15) {
    const at = atLocal(now, 0, 16);
    out.push({ id: 'today', label: `Today ${formatTime(at)}`, sub: 'Your time', at, icon: 'clock' });
  }
  const tomorrow = atLocal(now, 1, 9);
  out.push({ id: 'tomorrow', label: `Tomorrow ${formatTime(tomorrow)}`, sub: formatDay(tomorrow), at: tomorrow, icon: 'clock' });
  const toMonday = (8 - now.getDay()) % 7 || 7;
  if (toMonday > 1) {
    const monday = atLocal(now, toMonday, 9);
    out.push({ id: 'monday', label: formatDayTime(monday), sub: 'Next week', at: monday, icon: 'calendar' });
  }
  return out;
}

/** "today at 16:00", "tomorrow at 09:00", "Mon 19 Oct 09:00". */
export function whenPhrase(at: Date, now = new Date()): string {
  const days = daysFromToday(at, now);
  if (days === 0) return `today at ${formatTime(at)}`;
  if (days === 1) return `tomorrow at ${formatTime(at)}`;
  return formatDayTime(at);
}

/** Why a chosen send time cannot be used, or null when it is fine. Matches the server's rules. */
export function scheduleProblem(at: Date | null, now = new Date()): string | null {
  if (!at) return 'Pick a date and a time';
  if (at.getTime() < now.getTime() + 60_000) return 'Pick a time at least a minute from now';
  if (at.getTime() > now.getTime() + 365 * 86_400_000) return 'Pick a time within the next year';
  return null;
}

/* ---------- attachments ---------- */

export function totalBytes(files: { size: number }[]): number {
  return files.reduce((sum, f) => sum + f.size, 0);
}

const LIMIT_SENTENCE = 'Gmail sends up to 25 MB per email, attachments included.';

/** Why these files cannot be added, or null when they fit within Gmail's limit for one email. */
export function sizeProblem(existing: number, adding: { name: string; size: number }[], max = MAX_MESSAGE_BYTES): string | null {
  const huge = adding.find((f) => f.size > max);
  if (huge) return `${huge.name} is ${formatBytes(huge.size)}. ${LIMIT_SENTENCE} Share a link to a file this large instead.`;
  const total = existing + totalBytes(adding);
  if (total > max) {
    const files = adding.length === 1 ? adding[0]!.name : plural(adding.length, 'file');
    return `With ${files}, this email comes to ${formatBytes(total)}. ${LIMIT_SENTENCE} Remove a file or share a link instead.`;
  }
  return null;
}

/** The server's reason without a sentence the callout title already says. */
export function withoutTitle(text: string | null, title: string): string | null {
  if (!text) return text;
  const t = title.replace(/\.$/, '').toLowerCase();
  const rest = text
    .split(/(?<=\.)\s+/)
    .filter((s) => s.replace(/\.$/, '').toLowerCase() !== t)
    .join(' ');
  return rest || text;
}

/** Keeps the files that fit under the limit, in the order given, and says why the rest were left out. */
export function fitFiles<T extends { name: string; size: number }>(existing: number, adding: T[], max = MAX_MESSAGE_BYTES): { fit: T[]; problem: string | null } {
  const fit: T[] = [];
  const left: T[] = [];
  let total = existing;
  for (const f of adding) {
    if (total + f.size <= max) {
      fit.push(f);
      total += f.size;
    } else left.push(f);
  }
  return { fit, problem: left.length ? sizeProblem(total, left, max) : null };
}

/** The short label on a file tile: "PDF", "DOC", "IMG". */
export function fileBadge(name: string, mime: string): { label: string; pdf: boolean } {
  const ext = (/\.([a-z0-9]{1,8})$/i.exec(name)?.[1] ?? '').toLowerCase();
  if (ext === 'pdf' || mime === 'application/pdf') return { label: 'PDF', pdf: true };
  if (mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'svg'].includes(ext)) return { label: 'IMG', pdf: false };
  if (['doc', 'docx', 'odt', 'rtf', 'pages'].includes(ext)) return { label: 'DOC', pdf: false };
  if (['xls', 'xlsx', 'ods', 'numbers'].includes(ext)) return { label: 'XLS', pdf: false };
  if (['ppt', 'pptx', 'odp', 'key'].includes(ext)) return { label: 'PPT', pdf: false };
  if (['zip', 'rar', '7z', 'gz', 'tar'].includes(ext)) return { label: 'ZIP', pdf: false };
  return { label: ext ? ext.slice(0, 4).toUpperCase() : 'FILE', pdf: false };
}

/* ---------- drafts ---------- */

export interface DraftRequest {
  mode?: ComposeMode;
  threadId?: string;
  emailId?: string;
  to?: { email: string }[];
  personId?: string;
  companyId?: string;
  dealId?: string;
  outboxId?: string;
}

/** Where a draft is kept: one per conversation and mode, per record, per scheduled email, or one for a blank email. */
export function draftKey(req: DraftRequest): string {
  if (req.outboxId) return `outbox:${req.outboxId}`;
  if (req.threadId) return `${req.mode && req.mode !== 'new' ? req.mode : 'reply'}:${req.threadId}${req.emailId ? `:${req.emailId}` : ''}`;
  if (req.dealId) return `deal:${req.dealId}`;
  if (req.personId) return `person:${req.personId}`;
  if (req.companyId) return `company:${req.companyId}`;
  if (req.to?.length) return `to:${req.to.map((r) => r.email.toLowerCase()).sort().join(',')}`;
  return 'new';
}

export interface DraftAttachment {
  kind: 'upload' | 'email';
  id?: string;
  emailId?: string;
  attachmentId?: string;
  name: string;
  size: number;
  mime: string;
}

export interface Draft {
  mailboxId: string | null;
  to: Rcpt[];
  cc: Rcpt[];
  bcc: Rcpt[];
  subject: string;
  html: string;
  attachments: DraftAttachment[];
  templateId: string | null;
  trackOpens: boolean;
  /** ISO time, or null to send straight away. */
  sendAt: string | null;
  savedAt: number;
}

/** What the composer knows about an email beyond what you type: what it answers, where it is filed, how it began. */
export interface ComposeBase {
  mode: ComposeMode;
  /** Changing a scheduled email (outboxId is set). */
  editing: boolean;
  /** Who the title names, such as "Reply to Hannah Whitcombe". */
  name: string | null;
  quote: { html: string; text: string } | null;
  replyToEmailId: string | null;
  forwardOfEmailId: string | null;
  threadId: string | null;
  personId: string | null;
  companyId: string | null;
  dealId: string | null;
  outboxId: string | null;
  /** The message the composer starts with, so an untouched draft is not kept. */
  starter: string;
}

export const DRAFT_DAYS = 30;
const PREFIX = 'clientbook.draft.';

interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  key(index: number): string | null;
  readonly length: number;
}

function storageKey(userId: string, key: string): string {
  return `${PREFIX}${userId}.${key}`;
}

/** True when a draft holds nothing worth keeping beyond what the composer starts with. */
export function draftIsEmpty(d: Pick<Draft, 'to' | 'cc' | 'bcc' | 'subject' | 'html' | 'attachments'>, starter = ''): boolean {
  const body = d.html === starter || isBlankHtml(d.html);
  return body && !d.subject.trim() && d.to.length + d.cc.length + d.bcc.length === 0 && d.attachments.length === 0;
}

export function readDraft(store: KeyValueStore, userId: string, key: string, now = Date.now()): Draft | null {
  try {
    const raw = store.getItem(storageKey(userId, key));
    if (!raw) return null;
    const d = JSON.parse(raw) as Draft;
    if (typeof d.savedAt !== 'number' || now - d.savedAt > DRAFT_DAYS * 86_400_000) {
      store.removeItem(storageKey(userId, key));
      return null;
    }
    return d;
  } catch {
    return null;
  }
}

export function writeDraft(store: KeyValueStore, userId: string, key: string, draft: Draft): void {
  try {
    store.setItem(storageKey(userId, key), JSON.stringify(draft));
  } catch {
    // A full or blocked storage keeps the draft in memory only.
  }
}

export function removeDraft(store: KeyValueStore, userId: string, key: string): void {
  try {
    store.removeItem(storageKey(userId, key));
  } catch {
    // Nothing to remove.
  }
}

/** Removes drafts older than DRAFT_DAYS. */
export function pruneDrafts(store: KeyValueStore, now = Date.now()): number {
  const stale: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const k = store.key(i);
    if (!k?.startsWith(PREFIX)) continue;
    try {
      const d = JSON.parse(store.getItem(k) ?? '{}') as Partial<Draft>;
      if (typeof d.savedAt !== 'number' || now - d.savedAt > DRAFT_DAYS * 86_400_000) stale.push(k);
    } catch {
      stale.push(k);
    }
  }
  for (const k of stale) store.removeItem(k);
  return stale.length;
}

/* ---------- list sends ---------- */

export interface LimitSummary {
  text: string;
  tone: 'ok' | 'near' | 'over';
  /** Copies that wait for the next day. */
  overflow: number;
}

/**
 * The sentence under the daily limit meter: "312 used today + 20 already scheduled + 43 here = 375 of 2,000. Well within
 * the limit." Gmail's limit counts a rolling 24 hours, so for a later start `used` is what went out in the 24 hours
 * before it, and `waiting` is what this inbox already has scheduled within a day of it.
 */
export function limitSummary(used: number, waiting: number, here: number, limit: number, later: boolean): LimitSummary {
  const taken = used + waiting;
  const after = taken + here;
  const parts = [later ? `${formatNumber(used)} sent in the 24 hours before` : `${formatNumber(used)} used today`];
  if (waiting > 0) parts.push(`${formatNumber(waiting)} already scheduled`);
  parts.push(`${formatNumber(here)} here`);
  const sum = `${parts.join(' + ')} = ${formatNumber(after)} of ${formatNumber(limit)}.`;
  const overflow = Math.max(0, after - Math.max(taken, limit));
  if (overflow > 0) {
    const rest = overflow === here ? 'They all go out' : `The last ${formatNumber(overflow)} go out`;
    return { text: `${sum} ${rest} a day later, when the inbox has room again.`, tone: 'over', overflow };
  }
  if (after > limit * 0.8) return { text: `${sum} Close to the limit.`, tone: 'near', overflow: 0 };
  return { text: `${sum} Well within the limit.`, tone: 'ok', overflow: 0 };
}

function dayPhrase(at: Date, now: Date): string {
  const days = daysFromToday(at, now);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `on ${formatDay(at)}`;
}

/** "Starts now, last copy goes out around 11:02 today" or "Starts Tue 20 Oct 10:00, finishes around 11:50". */
export function pacingNote(start: Date | null, finish: Date | null, count: number, now = new Date()): string {
  const begin = start ?? now;
  if (count <= 1 || !finish) return start ? `Goes out ${whenPhrase(begin, now)}` : 'Goes out in a few seconds';
  if (!start) return `Starts now, last copy goes out around ${formatTime(finish)} ${dayPhrase(finish, now)}`;
  const sameDay = daysFromToday(finish, begin) === 0;
  return `Starts ${formatDayTime(begin)}, finishes around ${formatTime(finish)}${sameDay ? '' : ` ${dayPhrase(finish, now)}`}`;
}

/** Bar heights for the pacing strip: one per gap between copies, taller for a longer gap. */
export function pacingBars(times: Date[], interval: number, max = 40): number[] {
  const out: number[] = [];
  for (let i = 1; i < times.length && out.length < max; i++) {
    const gap = (times[i]!.getTime() - times[i - 1]!.getTime()) / 1000;
    out.push(Math.round(Math.max(24, Math.min(100, (gap / (interval * 1.5)) * 100))));
  }
  return out;
}

/** The confirmation after starting a list send. */
export function listSentMessage(count: number, interval: number, startAt: Date | null): string {
  const copies = plural(count, 'personal copy', 'personal copies');
  if (startAt) return `${copies} ${count === 1 ? 'is' : 'are'} scheduled for ${whenPhrase(startAt)}`;
  if (count === 1) return `${copies} is on its way`;
  const every = intervalLabel(interval);
  return `${copies} are on their way · ${every[0]!.toLowerCase()}${every.slice(1)}`;
}
